/**
 * FathomVoyage — Module 1: Global Bunker Price Monitor
 */

import React, { useState, useMemo, useEffect, Component } from 'react';
import { LoadingSkeleton }                        from '../../components/DataBanner.jsx';
import { getBunkerData }                          from '../../services/bunker/index.js';
import BunkerComparison                           from './BunkerComparison.jsx';
import PaywallBlur                                from './PaywallBlur.jsx';
import HubMap                                     from '../BunkerPrices/HubMap.jsx';
import TimeRangeChips                             from '../BunkerPrices/TimeRangeChips.jsx';
import ProWaitlistModal                           from '../BunkerPrices/ProWaitlistModal.jsx';
import {
  PORTS_DATA, PORT_ORDER, FUEL_TYPES, FUEL_META,
  ROUTES, HEATMAP_PORTS, FORWARD_CURVES,
} from '../../services/bunker/mockData.js';
import { changeClass, changeArrow } from '../../utils/formatters.js';

// ─── Constants ────────────────────────────────────────────────────────────────

const WORKER_URL = (import.meta.env.VITE_BUNKER_WORKER_URL || '').replace(/\/$/, '');

const LIVE_FUELS  = ['vlsfo', 'mgo', 'ifo380'];
const FUEL_COLORS = { vlsfo: '#4F46E5', mgo: '#059669', ifo380: '#C2410C' };

const PORT_FLAGS = {
  'Singapore':       '🇸🇬',
  'Rotterdam':       '🇳🇱',
  'Houston':         '🇺🇸',
  'Fujairah':        '🇦🇪',
  'LA / Long Beach': '🇺🇸',
  'Hong Kong':       '🇭🇰',
  'New York':        '🇺🇸',
  'Santos':          '🇧🇷',
};

const AVG_CARD_DEF = [
  {
    key: 'global_20', label: 'Global 20',
    subs: [{ key: 'global_4', label: 'Global 4' }, { key: 'global_avg', label: 'World Avg' }],
  },
  { key: 'americas', label: 'Americas', subs: [] },
  { key: 'apac',     label: 'APAC',     subs: [] },
  { key: 'emea',     label: 'EMEA',     subs: [] },
];

// Placeholder port rows shown during loading / error states — same shape as buildTablePorts output
const PLACEHOLDER_PORTS = (() => {
  const names = ['Singapore', 'Rotterdam', 'Houston', 'Fujairah', 'LA / Long Beach', 'Hong Kong', 'New York', 'Santos'];
  const NULL_FUEL = { price: null, change: null };
  const ports = {};
  for (const name of names) {
    ports[name] = {
      name, region: '—', flag: PORT_FLAGS[name] || '\u{1F30D}',
      VLSFO: NULL_FUEL, MGO: NULL_FUEL, IFO380: NULL_FUEL,
      HSFO:  NULL_FUEL, LNG: NULL_FUEL, B20:    NULL_FUEL, B30: NULL_FUEL,
    };
  }
  return { ports, order: names };
})();

const FUEL_META_EXT = {
  ...FUEL_META,
  IFO380: { label: 'IFO 380', unit: '$/mt', description: 'Intermediate Fuel Oil 380 cSt' },
};

// ─── CSS ──────────────────────────────────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('fv-bunker-css')) {
  const s = document.createElement('style');
  s.id = 'fv-bunker-css';
  s.textContent = `
    /* Layout */
    .fv-top-row  { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    .fv-fwd-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
    @media (max-width: 900px) {
      .fv-top-row  { grid-template-columns: 1fr; }
      .fv-fwd-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 500px) { .fv-fwd-grid { grid-template-columns: 1fr; } }

    /* Table */
    .fv-tbl-row td { transition: background 0.1s; cursor: pointer; }
    .fv-tbl-row:hover td { background: #1a2640; }
    .fv-tbl-row.expanded td { background: #1e2d44; }
    .fv-fuel-tab { transition: all 0.15s; }
    .fv-fuel-tab:hover { opacity: 1 !important; }
    .fv-row-chevron {
      display: inline-block; transition: transform 0.18s;
      color: #3a4558; font-size: 18px; line-height: 1; flex-shrink: 0;
    }
    .fv-row-chevron.open { transform: rotate(90deg); color: #8892a5; }

    /* Regional avg strip */
    .fv-avg-strip { padding: 0 28px 20px; background: #111a2e; border-bottom: 1px solid #1e2d44; }
    .fv-avg-fuel-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px 0 14px;
    }
    .fv-avg-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
    @media (max-width: 700px) { .fv-avg-cards { grid-template-columns: repeat(2, 1fr); } }
    .fv-avg-card {
      padding: 14px 16px; border-radius: 9px;
      border: 1px solid #1e2d44; background: #0b0f1c;
      display: flex; flex-direction: column; gap: 4px;
    }
    .fv-avg-card-label {
      font-size: 10px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.7px; color: #8892a5; font-family: 'Figtree', sans-serif;
    }
    .fv-avg-card-price {
      font-family: 'JetBrains Mono', monospace;
      font-size: 24px; font-weight: 500; color: #fff; letter-spacing: -0.5px;
      margin: 2px 0;
    }
    .fv-avg-card-change {
      font-family: 'JetBrains Mono', monospace;
      font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 3px;
    }
    .fv-avg-card-unit {
      font-family: 'Figtree', sans-serif; font-size: 10px; color: #8892a5; margin-top: 2px;
    }
    .fv-avg-card-sub {
      display: flex; justify-content: space-between;
      font-size: 11px; color: #8892a5; font-family: 'Figtree', sans-serif;
      padding-top: 4px; border-top: 1px solid #1e2d44; margin-top: 4px;
    }
    .fv-avg-card-sub span {
      font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #b8cae0;
    }

    /* Status indicator pulse */
    @keyframes bkPulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.25; }
    }

    /* Row expansion */
    .fv-expand-row td { padding: 0 !important; border-bottom: 2px solid #1e2d44 !important; }
    .fv-expand-panel { padding: 16px 24px 20px; background: #0b0f1c; }
    .fv-expand-header {
      display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px;
    }
    .fv-expand-range-btns { display: flex; gap: 5px; }
    .fv-expand-range-btn {
      padding: 3px 10px; border-radius: 5px; font-size: 11px; font-weight: 600;
      cursor: pointer; font-family: 'JetBrains Mono', monospace;
      border: 1px solid; transition: all 0.12s;
    }
    .fv-expand-charts {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 12px; margin-bottom: 16px;
    }
    @media (max-width: 600px) { .fv-expand-charts { grid-template-columns: 1fr; } }
    .fv-expand-chart-wrap {
      display: flex; flex-direction: column; align-items: center; gap: 6px;
      padding: 12px 10px; background: #111a2e; border-radius: 8px; border: 1px solid #1e2d44;
    }
    .fv-expand-stats {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 8px; border-top: 1px solid #1e2d44; padding-top: 12px;
    }
    .fv-expand-stat-cell { display: flex; flex-direction: column; align-items: center; gap: 5px; }
    .fv-expand-stat-nums { display: flex; gap: 14px; }
    .fv-expand-stat-item { text-align: center; }
  `;
  document.head.appendChild(s);
}

// ─── Style tokens ──────────────────────────────────────────────────────────────

const C = {
  VLSFO:  { color: '#4F46E5', bg: '#EEF2FF' },
  HSFO:   { color: '#C2410C', bg: '#FFF7ED' },
  MGO:    { color: '#059669', bg: '#D1FAE5' },
  IFO380: { color: '#C2410C', bg: '#FFF7ED' },
  LNG:    { color: '#0369A1', bg: '#F0F9FF' },
  B20:    { color: '#7C3AED', bg: '#F5F3FF' },
  B30:    { color: '#9D174D', bg: '#FDF2F8' },
};

const S = {
  header: {
    background: '#111a2e', padding: '18px 28px 16px',
    borderBottom: '1px solid #1e2d44',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
  },
  moduleNum:   { fontFamily:"'JetBrains Mono', monospace", fontSize:10, textTransform:'uppercase', letterSpacing:'1px', color:'#e9a020', fontWeight:500, marginBottom:4 },
  moduleTitle: { fontFamily:"'Fraunces', serif", fontSize:20, fontWeight:600, color:'#fff', letterSpacing:'-0.4px' },
  moduleSub:   { fontSize:12, color:'#8892a5', marginTop:2 },
  actions:     { display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' },
  btn:         { display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:7, border:'1px solid #1e2d44', background:'#111a2e', color:'#8892a5', fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:"'Figtree', sans-serif" },
  btnPrimary:  { background:'#e9a020', color:'#0b0f1c', borderColor:'#e9a020' },
  dataTag:     { fontFamily:"'JetBrains Mono', monospace", fontSize:10, padding:'3px 10px', borderRadius:20, background:'rgba(245,158,11,0.15)', color:'#f59e0b', fontWeight:500 },
  content:     { padding:'20px 28px', display:'flex', flexDirection:'column', gap:20 },
  card:        { background:'#111a2e', borderRadius:10, border:'1px solid #1e2d44', overflow:'hidden' },
  cardHeader:  { padding:'14px 20px', borderBottom:'1px solid #1e2d44', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' },
  cardTitle:   { fontFamily:"'Fraunces', serif", fontSize:13, fontWeight:600, color:'#fff' },
  cardMeta:    { fontSize:11, color:'#8892a5' },
  footer:      { padding:'14px 28px', borderTop:'1px solid #1e2d44', background:'#111a2e', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 },
};

const selectStyle = {
  width:'100%', padding:'8px 12px', border:'1px solid #1e2d44', borderRadius:7,
  background:'#0b0f1c', color:'#b8cae0', fontFamily:"'Figtree', sans-serif", fontSize:13,
  outline:'none', cursor:'pointer', appearance:'none',
  backgroundImage:`url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%238892a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
  backgroundRepeat:'no-repeat', backgroundPosition:'right 10px center', paddingRight:28,
};
const labelStyle = { fontSize:10, textTransform:'uppercase', letterSpacing:'0.7px', fontWeight:600, color:'#8892a5', marginBottom:5 };

// ─── localStorage helpers ─────────────────────────────────────────────────────

function readLS(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}
function writeLS(key, val) {
  try { localStorage.setItem(key, val); } catch {}
}

// ─── Worker fetch helpers ──────────────────────────────────────────────────────

async function fetchLiveData() {
  if (!WORKER_URL) {
    console.warn('[bunker] VITE_BUNKER_WORKER_URL not set — live data disabled');
    return null;
  }
  try {
    const res = await fetch(`${WORKER_URL}/api/bunker-prices`);
    if (!res.ok) {
      console.error(`[bunker] Worker responded ${res.status} ${res.statusText}`);
      return null;
    }
    const data = await res.json();
    if (!data?.ports?.length) {
      console.error('[bunker] Worker response missing ports array:', data);
      return null;
    }
    console.log(`[bunker] Live data loaded: ${data.ports.length} ports, updated ${data.updated_at}`);
    return data;
  } catch (err) {
    console.error('[bunker] Fetch failed:', err.name, err.message);
    return null;
  }
}

async function fetchHistoryData() {
  if (!WORKER_URL) return null;
  try {
    const res = await fetch(`${WORKER_URL}/api/bunker-prices/history`);
    return res.ok ? res.json() : null;
  } catch { return null; }
}

// ─── Data helpers ─────────────────────────────────────────────────────────────

function buildTablePorts(workerPorts) {
  const ports = {};
  const order = [];
  for (const p of workerPorts) {
    ports[p.port] = {
      name:   p.port,
      region: p.region,
      flag:   PORT_FLAGS[p.port] || '🌍',
      VLSFO:  { price: p.vlsfo,  change: p.vlsfo_change  },
      MGO:    { price: p.mgo,    change: p.mgo_change    },
      IFO380: { price: p.ifo380, change: p.ifo380_change },
      HSFO:   { price: null, change: null },
      LNG:    { price: null, change: null },
      B20:    { price: null, change: null },
      B30:    { price: null, change: null },
    };
    order.push(p.port);
  }
  return { ports, order };
}

function computeMockAverages() {
  const AMERICAS = ['HOU', 'PAN', 'BAL', 'LPA'];
  const APAC     = ['SGP', 'TPP', 'SHA', 'BUS', 'HKG', 'KHH', 'CMB'];
  const EMEA     = ['RTM', 'PIR', 'GIB', 'ALG', 'FUJ', 'JED', 'DUR', 'CPT'];

  const avg = (ids, fuel) => {
    const prices = ids.map(id => PORTS_DATA[id]?.[fuel]?.price).filter(Boolean);
    return prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : null;
  };

  const make = (ids) => ({
    vlsfo: avg(ids, 'VLSFO'), vlsfo_change: null,
    mgo:   avg(ids, 'MGO'),   mgo_change:   null,
    ifo380: null,             ifo380_change: null,
  });

  return {
    global_20:  make(PORT_ORDER.slice(0, 20)),
    global_4:   make(PORT_ORDER.slice(0, 4)),
    global_avg: make(PORT_ORDER),
    americas:   make(AMERICAS),
    apac:       make(APAC),
    emea:       make(EMEA),
  };
}

function statsFromArr(arr) {
  const valid = arr.filter(v => v != null);
  if (!valid.length) return { high: null, low: null };
  return { high: Math.max(...valid), low: Math.min(...valid) };
}

// ─── Mini SVG Line Chart ───────────────────────────────────────────────────────

function MiniLineChart({ data, label, color, portKey }) {
  const W = 150, H = 56, PAD = 4;

  const valid = (data || []).filter(v => v != null);
  if (valid.length < 2) {
    return (
      <div className="fv-expand-chart-wrap">
        <div style={{ fontSize:10, fontWeight:700, color, fontFamily:"'JetBrains Mono', monospace", textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
        <div style={{ height:H, display:'flex', alignItems:'center', justifyContent:'center', color:'#3a4558', fontSize:11, fontFamily:"'Figtree', sans-serif" }}>No data</div>
      </div>
    );
  }

  const min   = Math.min(...valid);
  const max   = Math.max(...valid);
  const range = max - min || 1;

  const pts = (data || []).map((v, i) => ({
    x: PAD + (i / ((data.length || 1) - 1)) * (W - PAD * 2),
    y: v == null ? null : (H - PAD) - ((v - min) / range) * (H - PAD * 2),
    v,
  })).filter(p => p.y != null);

  const lineStr = pts.map(p => `${p.x},${p.y}`).join(' ');
  const fillStr = pts.length
    ? `${pts[0].x},${H - PAD} ${lineStr} ${pts[pts.length - 1].x},${H - PAD}`
    : '';
  const last    = pts[pts.length - 1];
  const gradId  = `bk-${(portKey || label).replace(/[^a-zA-Z0-9]/g, '')}-${label}`;

  return (
    <div className="fv-expand-chart-wrap">
      <div style={{ fontSize:10, fontWeight:700, color, fontFamily:"'JetBrains Mono', monospace", textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
      <svg width={W} height={H} style={{ overflow:'visible' }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity={0.15} />
            <stop offset="100%" stopColor={color} stopOpacity={0}    />
          </linearGradient>
        </defs>
        {fillStr && <polygon points={fillStr} fill={`url(#${gradId})`} />}
        <polyline
          points={lineStr} fill="none" stroke={color}
          strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round"
        />
        {last && <circle cx={last.x} cy={last.y} r={2.5} fill={color} />}
      </svg>
      <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:13, fontWeight:500, color:'#fff' }}>
        {last?.v != null ? `$${last.v}` : '—'}
      </div>
    </div>
  );
}

// ─── Regional Average Strip ───────────────────────────────────────────────────

function RegionalAvgStrip({ averages, activeFuel, onFuelChange, loading }) {
  const fKey  = activeFuel;                // already lowercase
  const chKey = fKey + '_change';

  return (
    <div className="fv-avg-strip">
      <div className="fv-avg-fuel-row">
        <span style={{ fontFamily:"'Figtree', sans-serif", fontSize:12, color:'#8892a5' }}>
          Regional Averages — USD/mt
        </span>
        <div style={{ display:'flex', gap:4 }}>
          {LIVE_FUELS.map(f => {
            const ac       = FUEL_COLORS[f];
            const isActive = activeFuel === f;
            return (
              <button key={f} onClick={() => onFuelChange(f)} style={{
                padding:'5px 13px', borderRadius:6, fontSize:11, fontWeight:700,
                cursor:'pointer', fontFamily:"'JetBrains Mono', monospace", letterSpacing:'0.3px',
                border:'none', transition:'all 0.15s',
                background: isActive ? (ac + '1A') : 'transparent',
                color:      isActive ? ac : '#8892a5',
                outline:    isActive ? `1.5px solid ${ac}44` : 'none',
              }}>
                {f.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="fv-avg-cards">
        {AVG_CARD_DEF.map(({ key, label, subs }) => {
          const avg    = averages?.[key];
          const price  = avg?.[fKey]  ?? null;
          const change = avg?.[chKey] ?? null;
          const isUp   = change != null && change > 0;
          const isDown = change != null && change < 0;

          return (
            <div key={key} className="fv-avg-card">
              <div className="fv-avg-card-label">{label}</div>
              {loading
                ? <div style={{ height:32, background:'#1e2d44', borderRadius:4, marginTop:4 }} />
                : <>
                    <div className="fv-avg-card-price">{price != null ? `$${price}` : '—'}</div>
                    {change != null && (
                      <div className="fv-avg-card-change" style={{ color: isUp ? '#DC2626' : isDown ? '#059669' : '#3a4558' }}>
                        {isUp ? '▲' : isDown ? '▼' : '—'} {isUp ? '+' : ''}{change}/mt
                      </div>
                    )}
                    <div className="fv-avg-card-unit">USD/mt · {activeFuel.toUpperCase()}</div>
                    {subs.map(sub => {
                      const sv = averages?.[sub.key]?.[fKey];
                      return (
                        <div key={sub.key} className="fv-avg-card-sub">
                          <span>{sub.label}</span>
                          <span>{sv != null ? `$${sv}` : '—'}</span>
                        </div>
                      );
                    })}
                  </>
              }
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Port History Expansion Panel ─────────────────────────────────────────────

function PortHistoryPanel({ portName, history, histLoading, range, onRangeChange, port, averages }) {
  const snapshots = useMemo(() => {
    if (!history?.snapshots) return [];
    return [...history.snapshots]
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-range);
  }, [history, range]);

  const portSeries = useMemo(() => snapshots.map(snap => {
    const p = snap.ports?.find(p => p.port === portName);
    return { date: snap.date, vlsfo: p?.vlsfo ?? null, mgo: p?.mgo ?? null, ifo380: p?.ifo380 ?? null };
  }), [snapshots, portName]);

  const vlsfoArr  = portSeries.map(d => d.vlsfo);
  const mgoArr    = portSeries.map(d => d.mgo);
  const ifo380Arr = portSeries.map(d => d.ifo380);

  const stats = {
    vlsfo:  statsFromArr(vlsfoArr),
    mgo:    statsFromArr(mgoArr),
    ifo380: statsFromArr(ifo380Arr),
  };

  // Spread vs. global average (negative = cheaper than average = good for shipowner)
  const spread = {
    vlsfo:  port?.VLSFO?.price  != null && averages?.global_avg?.vlsfo  != null
      ? Math.round((port.VLSFO.price  - averages.global_avg.vlsfo)  * 10) / 10 : null,
    mgo:    port?.MGO?.price    != null && averages?.global_avg?.mgo    != null
      ? Math.round((port.MGO.price    - averages.global_avg.mgo)    * 10) / 10 : null,
    ifo380: port?.IFO380?.price != null && averages?.global_avg?.ifo380 != null
      ? Math.round((port.IFO380.price - averages.global_avg.ifo380) * 10) / 10 : null,
  };

  if (histLoading) {
    return <div className="fv-expand-panel"><LoadingSkeleton rows={3} /></div>;
  }

  if (!history) {
    return (
      <div className="fv-expand-panel" style={{ textAlign:'center', color:'#8892a5', fontSize:12, fontFamily:"'Figtree', sans-serif", fontStyle:'italic', padding:'20px 24px' }}>
        Price history requires a live Worker connection — set VITE_BUNKER_WORKER_URL.
      </div>
    );
  }

  return (
    <div className="fv-expand-panel">
      {/* Range selector */}
      <div className="fv-expand-header">
        <span style={{ fontSize:11, color:'#8892a5', fontFamily:"'Figtree', sans-serif" }}>
          Price history — {portName}
        </span>
        <div className="fv-expand-range-btns">
          {[7, 14, 30].map(r => (
            <button key={r} onClick={() => onRangeChange(r)} className="fv-expand-range-btn" style={{
              borderColor: range === r ? '#e9a020' : '#1e2d44',
              background:  range === r ? 'rgba(233,160,32,0.15)' : 'transparent',
              color:       range === r ? '#e9a020' : '#8892a5',
            }}>
              {r}d
            </button>
          ))}
        </div>
      </div>

      {/* Three charts */}
      <div className="fv-expand-charts">
        <MiniLineChart data={vlsfoArr}  label="VLSFO"  color={FUEL_COLORS.vlsfo}  portKey={portName} />
        <MiniLineChart data={mgoArr}    label="MGO"    color={FUEL_COLORS.mgo}    portKey={portName} />
        <MiniLineChart data={ifo380Arr} label="IFO380" color={FUEL_COLORS.ifo380} portKey={portName} />
      </div>

      {/* Stats */}
      <div className="fv-expand-stats">
        {LIVE_FUELS.map(f => {
          const s  = stats[f];
          const sp = spread[f];
          return (
            <div key={f} className="fv-expand-stat-cell">
              <div style={{ fontSize:10, fontWeight:700, color:FUEL_COLORS[f], fontFamily:"'JetBrains Mono', monospace", textTransform:'uppercase', letterSpacing:'0.5px' }}>{f}</div>
              <div className="fv-expand-stat-nums">
                <div className="fv-expand-stat-item">
                  <div style={{ fontSize:9, color:'#8892a5', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px', marginBottom:2 }}>{range}d High</div>
                  <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:12, color:'#DC2626' }}>{s.high != null ? `$${s.high}` : '—'}</div>
                </div>
                <div className="fv-expand-stat-item">
                  <div style={{ fontSize:9, color:'#8892a5', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px', marginBottom:2 }}>{range}d Low</div>
                  <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:12, color:'#059669' }}>{s.low != null ? `$${s.low}` : '—'}</div>
                </div>
                {sp != null && (
                  <div className="fv-expand-stat-item">
                    <div style={{ fontSize:9, color:'#8892a5', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px', marginBottom:2 }}>vs Avg</div>
                    <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:12, color: sp <= 0 ? '#059669' : '#DC2626' }}>
                      {sp > 0 ? '+' : ''}{sp}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Fuel Tab Bar (used by SpreadHeatmap) ─────────────────────────────────────

function FuelTabs({ active, onChange, types = FUEL_TYPES }) {
  return (
    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
      {types.map(f => {
        const fc       = C[f] || { color:'#8892a5', bg:'transparent' };
        const isActive = active === f;
        return (
          <button key={f} className="fv-fuel-tab" onClick={() => onChange(f)} style={{
            padding:'4px 10px', borderRadius:5, fontSize:11, fontWeight:600,
            cursor:'pointer', fontFamily:"'JetBrains Mono', monospace", letterSpacing:'0.3px',
            border:'none', background: isActive ? fc.color + '28' : 'transparent',
            color: isActive ? fc.color : '#3a4558',
          }}>
            {FUEL_META_EXT[f]?.label || f}
          </button>
        );
      })}
    </div>
  );
}

// ─── Price Table ──────────────────────────────────────────────────────────────

const PAYWALLED_FUELS = new Set(['HSFO', 'LNG', 'B20', 'B30']);

function PriceTable({ ports, portOrder, activeFuel, loading, averages, openWaitlistForm }) {
  const [sortBy,       setSortBy]       = useState(activeFuel.toUpperCase());
  const [search,       setSearch]       = useState('');
  const [fuelSet,      setFuelSet]      = useState(['VLSFO', 'MGO', 'IFO380']);
  const [expandedPort, setExpandedPort] = useState(null);
  const [historyRange, setHistoryRange] = useState(14);
  const [history,      setHistory]      = useState(null);
  const [histLoading,  setHistLoading]  = useState(false);

  const DISPLAY_FUELS = ['VLSFO', 'MGO', 'IFO380', 'HSFO', 'LNG', 'B20', 'B30'];

  // Keep sort column synced with hero strip fuel selection
  useEffect(() => { setSortBy(activeFuel.toUpperCase()); }, [activeFuel]);

  // Lazy-load history on first row expansion; cache for session lifetime
  useEffect(() => {
    if (expandedPort && !history && !histLoading) {
      setHistLoading(true);
      fetchHistoryData().then(h => {
        setHistory(h);
        setHistLoading(false);
      });
    }
  }, [expandedPort, history, histLoading]);

  const sortedPorts = useMemo(() => [...portOrder]
    .filter(id => {
      if (!search) return true;
      const q = search.toLowerCase();
      return ports[id]?.name.toLowerCase().includes(q) ||
             ports[id]?.region.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const pa = ports[a]?.[sortBy]?.price;
      const pb = ports[b]?.[sortBy]?.price;
      if (pa == null && pb == null) return 0;
      if (pa == null) return 1;
      if (pb == null) return -1;
      return pa - pb;
    }),
  [sortBy, ports, portOrder, search]);

  const cheapestByFuel = useMemo(() => {
    const result = {};
    DISPLAY_FUELS.forEach(f => {
      const prices = portOrder.map(id => ports[id]?.[f]?.price).filter(Boolean);
      result[f] = prices.length ? { min: Math.min(...prices), max: Math.max(...prices) } : { min: 0, max: 0 };
    });
    return result;
  }, [ports, portOrder]);

  const visibleFuels = DISPLAY_FUELS.filter(f => fuelSet.includes(f));

  if (loading) return <div style={S.card}><LoadingSkeleton rows={8} /></div>;

  const thStyle = { padding:'10px 14px', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.7px', color:'#8892a5', background:'#0b0f1c', borderBottom:'1px solid #1e2d44', textAlign:'left', whiteSpace:'nowrap' };
  const tdStyle = { padding:'12px 14px', borderBottom:'1px solid #1e2d44', verticalAlign:'middle' };

  const handleRowClick = (portKey) => {
    setExpandedPort(prev => prev === portKey ? null : portKey);
  };

  return (
    <div style={S.card}>
      {/* Controls */}
      <div style={{ ...S.cardHeader, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={S.cardTitle}>Hub Ports Overview</div>
          <div style={S.cardMeta}>
            {/* {sortedPorts.length} of {portOrder.length} ports ·  */}
            Click a row to expand history · Green = cheapest
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ position:'relative' }}>
            <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'#3a4558', pointerEvents:'none' }}>🔍</span>
            <input
              type="text"
              placeholder="Search port or region…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding:'7px 12px 7px 32px', border:'1px solid #1e2d44', borderRadius:7, fontSize:13, outline:'none', width:200, fontFamily:"'Figtree', sans-serif", color:'#b8cae0', background:'#0b0f1c' }}
            />
          </div>
          {/* Column visibility toggles */}
          <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
            {DISPLAY_FUELS.map(f => {
              const fc = C[f] || { color:'#8892a5', bg:'transparent' };
              const on = fuelSet.includes(f);
              return (
                <button key={f} onClick={() => setFuelSet(prev => on ? prev.filter(x => x !== f) : [...prev, f])} style={{
                  padding:'4px 8px', borderRadius:5, fontSize:10, fontWeight:600,
                  cursor:'pointer', border:`1px solid ${on ? fc.color + '55' : '#1e2d44'}`,
                  background: on ? fc.color + '22' : 'transparent',
                  color:      on ? fc.color : '#3a4558',
                  fontFamily:"'JetBrains Mono', monospace",
                }}>
                  {f}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:520 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width:200 }}>Port</th>
              {visibleFuels.map(f => (
                <th key={f}
                  style={{ ...thStyle, textAlign:'center', minWidth:110, cursor:'pointer' }}
                  onClick={() => setSortBy(f)}>
                  <span style={{ color: sortBy === f ? (C[f]?.color || '#b8cae0') : '#8892a5' }}>
                    {FUEL_META_EXT[f]?.label || f} {sortBy === f ? '↑' : ''}
                  </span>
                  {f.startsWith('B') && <div style={{ fontSize:9, color:'#3a4558', fontWeight:400, marginTop:2 }}>select ports only</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPorts.length === 0 && (
              <tr>
                <td colSpan={visibleFuels.length + 1} style={{ padding:24, textAlign:'center', color:'#8892a5', fontSize:13 }}>
                  No ports match "{search}"
                </td>
              </tr>
            )}
            {sortedPorts.map((portKey, idx) => {
              const port       = ports[portKey];
              if (!port) return null;
              const isExpanded = expandedPort === portKey;

              return (
                <React.Fragment key={portKey}>
                  <tr
                    className={`fv-tbl-row${isExpanded ? ' expanded' : ''}`}
                    onClick={() => handleRowClick(portKey)}
                  >
                    <td style={tdStyle}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ fontSize:18, lineHeight:1 }}>{port.flag}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:15, fontWeight:500, color:'#fff', whiteSpace:'nowrap' }}>
                            {sortBy === 'VLSFO' && idx < 3 && (
                              <span style={{
                                display:'inline-flex', alignItems:'center', justifyContent:'center',
                                width:18, height:18, borderRadius:4, marginRight:6,
                                background: idx === 0 ? 'rgba(233,160,32,0.2)' : '#1e2d44',
                                color:      idx === 0 ? '#e9a020' : '#8892a5',
                                fontFamily:"'JetBrains Mono', monospace", fontSize:10, fontWeight:600,
                              }}>
                                {idx === 0 ? '★' : `#${idx + 1}`}
                              </span>
                            )}
                            {port.name}
                          </div>
                          <div style={{ fontSize:11, color:'#8892a5' }}>{port.region}</div>
                        </div>
                        <span className={`fv-row-chevron${isExpanded ? ' open' : ''}`}>›</span>
                      </div>
                    </td>

                    {visibleFuels.map(fuel => {
                      // Paywalled fuel grades: blur placeholder + single CTA on the middle row
                      if (PAYWALLED_FUELS.has(fuel)) {
                        const midIdx = Math.floor(sortedPorts.length / 2);
                        const isMiddle = idx === midIdx;
                        return (
                          <td key={fuel} style={{ ...tdStyle, padding: 0, textAlign: 'center', position: 'relative', minWidth: 110 }}>
                            {/* Blurred placeholder */}
                            <div style={{
                              filter: 'blur(10px)', userSelect: 'none', pointerEvents: 'none',
                              padding: '12px 14px', fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 14, color: '#b8cae0',
                            }}>
                              $—
                            </div>
                            {/* Dark overlay — absorbs clicks, shows CTA on middle row */}
                            <div style={{
                              position: 'absolute', inset: 0,
                              background: 'rgba(7, 9, 15, 0.55)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isMiddle && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openWaitlistForm?.(`fuel_grade_${fuel.toLowerCase()}`); }}
                                  style={{
                                    padding: '5px 10px', background: '#e9a020', color: '#0b0f1c',
                                    border: 'none', borderRadius: 6,
                                    fontFamily: "'Fraunces', serif", fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                  }}
                                >
                                  🔒 Unlock
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      }

                      const d = port[fuel];
                      if (!d || d.price == null) return (
                        <td key={fuel} style={{ ...tdStyle, textAlign:'center' }}>
                          <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:11, color:'#3a4558' }}>—</span>
                        </td>
                      );
                      const isCheapest = d.price === cheapestByFuel[fuel]?.min;
                      const isMostExp  = d.price === cheapestByFuel[fuel]?.max;
                      const cc         = changeClass(d.change);
                      return (
                        <td key={fuel} style={{ ...tdStyle, textAlign:'center' }}>
                          <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                            <div style={{
                              fontFamily:"'JetBrains Mono', monospace", fontSize:15,
                              fontWeight: isCheapest ? 700 : 500,
                              color: isCheapest ? '#2daa6e' : isMostExp ? '#ef4444' : '#b8cae0',
                            }}>
                              {isCheapest && <span style={{ fontSize:11, marginRight:2 }}>★</span>}
                              ${d.price}
                            </div>
                            <div style={{
                              display:'flex', alignItems:'center', gap:2,
                              fontFamily:"'JetBrains Mono', monospace", fontSize:11, fontWeight:500,
                              color: cc === 'up' ? '#ef4444' : cc === 'down' ? '#2daa6e' : '#3a4558',
                            }}>
                              {d.change != null ? changeArrow(d.change) : ''}{d.change != null ? Math.abs(d.change) : '—'}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Accordion expansion */}
                  {isExpanded && (
                    <tr className="fv-expand-row">
                      <td colSpan={visibleFuels.length + 1}>
                        <PortHistoryPanel
                          portName={port.name}
                          history={history}
                          histLoading={histLoading}
                          range={historyRange}
                          onRangeChange={setHistoryRange}
                          port={port}
                          averages={averages}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Cheapest Port Finder ─────────────────────────────────────────────────────

function CheapestPortFinder({ ports, openWaitlistForm }) {
  const [route,   setRoute]   = useState('ag_fareast');
  const [fuel,    setFuel]    = useState('VLSFO');
  const [liftMT,  setLift]    = useState(1000);
  const [results, setResults] = useState(null);

  const find = () => {
    const r = ROUTES[route];
    const ranked = r.ports
      .filter(id => ports[id]?.[fuel]?.price != null)
      .map(id => ({ id, port: ports[id], price: ports[id][fuel].price, change: ports[id][fuel].change }))
      .sort((a, b) => a.price - b.price);
    const maxPrice = ranked[ranked.length - 1]?.price || 0;
    setResults({ ranked, maxPrice, route: r, fuel, liftMT: parseInt(liftMT) });
  };

  useEffect(() => { find(); }, []);

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>Cheapest Port Finder</div>
          <div style={S.cardMeta}>Optimal bunkering point on your route</div>
        </div>
      </div>
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:14 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <div style={labelStyle}>Route</div>
            <select style={selectStyle} value={route} onChange={e => { setRoute(e.target.value); setResults(null); }}>
              {Object.entries(ROUTES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Fuel Type</div>
            <select style={selectStyle} value={fuel} onChange={e => { setFuel(e.target.value); setResults(null); }}>
              {FUEL_TYPES.map(f => <option key={f} value={f}>{FUEL_META[f]?.label || f}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Lift Volume (MT)</div>
            <select style={selectStyle} value={liftMT} onChange={e => { setLift(e.target.value); setResults(null); }}>
              {[500, 750, 1000, 1500, 2000, 3000, 5000].map(v => <option key={v} value={v}>{v.toLocaleString()} MT</option>)}
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
            <button onClick={find} style={{ width:'100%', padding:'9px 16px', background:'#e9a020', color:'#0b0f1c', border:'none', borderRadius:7, fontFamily:"'Fraunces', serif", fontSize:13, fontWeight:500, cursor:'pointer' }}>
              Find Cheapest →
            </button>
          </div>
        </div>

        {/* Results are paywalled — join the waitlist to access detailed insights and unlock savings on your next bunker lift. */}

        <PaywallBlur interestSource="cheapest_bunkering_port" openWaitlistForm={openWaitlistForm}>
          {results?.ranked.length > 0 && (() => {
            const r = results;
            return (
              <>
                <div style={{ fontSize:10, color:'#8892a5', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  {ROUTES[route]?.description} · Top {Math.min(r.ranked.length, 3)} options
                </div>
                {r.ranked.slice(0, 3).map((item, idx) => {
                  const saving = r.maxPrice - item.price;
                  return (
                    <div key={item.id} style={{
                      padding:'12px 14px', borderRadius:8,
                      border: `1px solid ${idx === 0 ? '#e9a020' : '#1e2d44'}`,
                      background: idx === 0 ? 'linear-gradient(135deg,rgba(233,160,32,0.1) 0%,#111a2e 100%)' : '#0b0f1c',
                      display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
                    }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:11, fontWeight:600, color: idx === 0 ? '#e9a020' : '#8892a5', minWidth:16 }}>
                          {idx === 0 ? '★' : `#${idx + 1}`}
                        </div>
                        <span style={{ fontSize:18 }}>{item.port.flag}</span>
                        <div>
                          <div style={{ fontSize:14, fontWeight:500, color:'#fff' }}>{item.port.name}</div>
                          <div style={{ fontSize:11, color:'#8892a5' }}>
                            {item.port.region}
                            {item.change != null && <span style={{ marginLeft:6, color: item.change > 0 ? '#DC2626' : '#059669', fontWeight:500 }}>{item.change > 0 ? '▲' : '▼'} {Math.abs(item.change)}/mt</span>}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:15, fontWeight:600, color:'#fff' }}>${item.price}/mt</div>
                        {saving > 0 && <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:11, color:'#059669', fontWeight:500 }}>Save ${saving}/mt</div>}
                        {saving > 0 && <div style={{ fontSize:10, color:'#8892a5' }}>${(saving * r.liftMT).toLocaleString()} on {r.liftMT.toLocaleString()}MT</div>}
                      </div>
                    </div>
                  );
                })}
                {r.ranked.length >= 2 && (
                  <div style={{ padding:'10px 14px', borderRadius:7, background:'rgba(45,170,110,0.1)', border:'1px solid rgba(45,170,110,0.25)', fontSize:12, color:'#2daa6e', fontWeight:500, display:'flex', gap:8, lineHeight:1.4 }}>
                    <span style={{ flexShrink:0 }}>💡</span>
                    <span>Bunkering <strong>{r.fuel}</strong> in <strong>{r.ranked[0].port.name}</strong> saves <strong>${r.maxPrice - r.ranked[0].price}/mt</strong> vs {r.ranked[r.ranked.length - 1].port.name} — <strong>${((r.maxPrice - r.ranked[0].price) * r.liftMT).toLocaleString()}</strong> on a {r.liftMT.toLocaleString()}MT lift.</span>
                  </div>
                )}
              </>
            );
          })()}
        </PaywallBlur>

      </div>
    </div>
  );
}

// ─── Spread Heatmap ───────────────────────────────────────────────────────────

function SpreadHeatmap({ ports, openWaitlistForm }) {
  const [fuel,    setFuel]    = useState('VLSFO');
  const [hovered, setHovered] = useState(null);
  const heatPorts = HEATMAP_PORTS.filter(id => ports[id]?.[fuel]?.price != null);

  const getColor = (diff) => {
    if (diff === 0) return { bg:'#1e2d44', txt:'#8892a5' };
    const abs = Math.min(Math.abs(diff), 70);
    const intensity = abs / 70;
    if (diff < 0) {
      const r = Math.round(209 - intensity * 160), g = Math.round(210 - intensity * 60), b = Math.round(218 - intensity * 130);
      return { bg:`rgb(${r},${g},${b})`, txt: intensity > 0.4 ? '#fff' : '#059669' };
    } else {
      const r = Math.round(220 - intensity * 20), g = Math.round(220 - intensity * 160), b = Math.round(220 - intensity * 160);
      return { bg:`rgb(${r},${g},${b})`, txt: intensity > 0.4 ? '#fff' : '#DC2626' };
    }
  };

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>Port Spread Heatmap</div>
          <div style={S.cardMeta}>Price differential ($/mt) · Hover for detail · Green = cheaper</div>
        </div>
        <FuelTabs active={fuel} onChange={setFuel} types={['VLSFO', 'HSFO', 'MGO']} />
      </div>

      <PaywallBlur interestSource="heat_map" openWaitlistForm={openWaitlistForm}>
        <div style={{ padding:16, overflowX:'auto' }}>
        <div style={{ display:'grid', gridTemplateColumns:`68px repeat(${heatPorts.length},52px)`, gap:3 }}>
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:3 }}>
            <span style={{ fontSize:9, color:'#3a4558' }}>ROW vs →</span>
          </div>
          {heatPorts.map(id => (
            <div key={id} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2, padding:'3px 2px' }}>
              <span style={{ fontSize:13 }}>{ports[id].flag}</span>
              <span style={{ fontSize:9, fontWeight:600, color:'#8892a5', textAlign:'center' }}>{ports[id].name.split(' ')[0].substring(0, 5)}</span>
            </div>
          ))}
          {heatPorts.map(rowId => (
            <React.Fragment key={rowId}>
              <div style={{ display:'flex', alignItems:'center', gap:5, paddingRight:4 }}>
                <span style={{ fontSize:13 }}>{ports[rowId].flag}</span>
                <span style={{ fontSize:10, fontWeight:600, color:'#8892a5', whiteSpace:'nowrap' }}>{ports[rowId].name.split(' ')[0].substring(0, 6)}</span>
              </div>
              {heatPorts.map(colId => {
                const rp   = ports[rowId]?.[fuel]?.price;
                const cp   = ports[colId]?.[fuel]?.price;
                const diff = rowId === colId ? 0 : rp - cp;
                const { bg, txt } = getColor(diff);
                const isHov = hovered?.row === rowId && hovered?.col === colId;
                return (
                  <div key={colId} style={{
                    width:52, height:42, borderRadius:5, display:'flex', flexDirection:'column',
                    alignItems:'center', justifyContent:'center', background:bg, color:txt,
                    border:`2px solid ${isHov ? '#e9a020' : 'transparent'}`,
                    cursor:'pointer', transition:'transform 0.12s',
                    transform: isHov ? 'scale(1.12)' : 'scale(1)', position:'relative', zIndex: isHov ? 10 : 1,
                  }}
                    onMouseEnter={() => setHovered({ row:rowId, col:colId })}
                    onMouseLeave={() => setHovered(null)}>
                    {rowId === colId
                      ? <span style={{ fontSize:8, opacity:0.4 }}>{ports[rowId].name.substring(0, 3).toUpperCase()}</span>
                      : <>
                          <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:11, fontWeight:500, lineHeight:1 }}>{diff > 0 ? '+' : ''}{diff}</span>
                          <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:7, opacity:0.7 }}>$/mt</span>
                        </>
                    }
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, fontSize:11, color:'#8892a5' }}>
          <span>Cheaper</span>
          <div style={{ flex:1, maxWidth:140, height:7, borderRadius:4, background:'linear-gradient(to right,#059669,#1e2d44,#DC2626)' }} />
          <span>More expensive</span>
        </div>
        {hovered && hovered.row !== hovered.col && (
          <div style={{ marginTop:8, padding:'7px 12px', borderRadius:7, background:'#0b0f1c', border:'1px solid #1e2d44', fontSize:12, color:'#b8cae0' }}>
            {(() => {
              const diff = ports[hovered.row]?.[fuel]?.price - ports[hovered.col]?.[fuel]?.price;
              return diff < 0
                ? `💚 ${ports[hovered.row].name} saves $${Math.abs(diff)}/mt vs ${ports[hovered.col].name}`
                : `🔴 ${ports[hovered.row].name} costs $${diff}/mt more than ${ports[hovered.col].name}`;
            })()}
          </div>
        )}
        </div>
      </PaywallBlur>
    </div>
  );
}

// ─── Forward Prices ───────────────────────────────────────────────────────────

function ForwardPrices({ openWaitlistForm }) {
  const ports = Object.keys(FORWARD_CURVES);
  const [activePort, setActivePort] = useState('SGP');
  const data = FORWARD_CURVES[activePort];

  const structureColor = { contango:'#D97706', backwardation:'#059669', hump:'#4F46E5' };
  const structureLabel = { contango:'Contango', backwardation:'Backwardation', hump:'Near-term Hump' };

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>VLSFO Forward Prices</div>
          <div style={S.cardMeta}>6-month forward curve · Indicative</div>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {ports.map(pid => {
            const p = FORWARD_CURVES[pid];
            return (
              <button key={pid} onClick={() => setActivePort(pid)} style={{
                padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:500,
                cursor:'pointer', fontFamily:"'Figtree', sans-serif",
                border:'1px solid', transition:'all 0.15s',
                borderColor: activePort === pid ? '#e9a020' : '#1e2d44',
                background:  activePort === pid ? 'rgba(233,160,32,0.15)' : 'transparent',
                color:       activePort === pid ? '#e9a020' : '#8892a5',
              }}>
                {p.flag} {p.name}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding:20 }}>
        {/* Spot price — paywalled  */}
        <PaywallBlur interestSource="forward_prices" openWaitlistForm={openWaitlistForm}>
        <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.7px', fontWeight:600, color:'#8892a5', marginBottom:3 }}>Spot Price</div>
            <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:26, fontWeight:500, color:'#fff' }}>${data.spot}/mt</div>
          </div>
          <div style={{ width:1, height:40, background:'#1e2d44' }} />
          <div>
            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'0.7px', fontWeight:600, color:'#8892a5', marginBottom:3 }}>Curve Structure</div>
            <div style={{
              display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px',
              borderRadius:20, fontSize:12, fontWeight:600,
              background: structureColor[data.structure] + '18',
              color:      structureColor[data.structure],
            }}>
              {structureLabel[data.structure]}
            </div>
          </div>
        </div>

        {/* Forward curve grid — paywalled */}
        
          <div>
            <div className="fv-fwd-grid">
              {data.curve.map((point, i) => {
                const isUp = point.change > 0;
                return (
                  <div key={i} style={{
                    padding:'14px 16px', borderRadius:9, border:'1px solid #1e2d44',
                    background: isUp ? 'rgba(217,119,6,0.1)' : 'rgba(5,150,105,0.1)',
                  }}>
                    <div style={{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', color:'#8892a5', marginBottom:6 }}>{point.month}</div>
                    <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:20, fontWeight:500, color:'#fff', marginBottom:4 }}>${point.price}</div>
                    <div style={{
                      fontFamily:"'JetBrains Mono', monospace", fontSize:12, fontWeight:600,
                      color: isUp ? '#D97706' : '#059669',
                      display:'flex', alignItems:'center', gap:3,
                    }}>
                      {isUp ? '▲' : '▼'} {isUp ? '+' : ''}{point.change}/mt vs spot
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </PaywallBlur>
      </div>
    </div>
  );
}

// ─── Error boundary ───────────────────────────────────────────────────────────

class BunkerErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: 32, background: '#0a1628', minHeight: '100vh' }}>
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '20px 24px', maxWidth: 600 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 600, color: '#ef4444', marginBottom: 8 }}>
            Component render error
          </div>
          <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#c4ccd8', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.error?.message}
            {'\n\n'}
            {this.state.error?.stack?.split('\n').slice(0, 6).join('\n')}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: 16, padding: '8px 18px', background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

// ─── Premium teaser ───────────────────────────────────────────────────────────

function PremiumTeaser({ onOpen }) {
  return (
    <div style={{ padding: '24px 32px', borderRadius: 14, background: '#151f35', border: '1px solid rgba(233,160,32,0.12)', textAlign: 'center' }}>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
        🔒 FathomVoyage Pro
      </div>
      <div style={{ fontSize: 14, color: '#8892a5', marginBottom: 20, lineHeight: 1.6 }}>
        Full coverage: 200+ ports · LNG &amp; Biofuel pricing · Real-time alerts
      </div>
      <button
        onClick={onOpen}
        onMouseEnter={e => e.currentTarget.style.background = '#00e6b8'}
        onMouseLeave={e => e.currentTarget.style.background = '#e9a020'}
        style={{ padding: '11px 24px', background: '#e9a020', color: '#0a1628', border: 'none', borderRadius: 8, fontFamily: "'Fraunces', serif", fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s' }}
      >
        Get Early Access — $49/mo
      </button>
    </div>
  );
}

// ─── Main Module ──────────────────────────────────────────────────────────────

function BunkerModuleInner() {
  const [data,      setData]      = useState(null);
  const [liveData,  setLiveData]  = useState(null);
  const [loadState, setLoadState] = useState(WORKER_URL ? 'loading' : 'error'); // 'loading'|'live'|'error'
  const [retryKey,  setRetryKey]  = useState(0);

  const [activeFuel,   setActiveFuelRaw] = useState(() => readLS('fv_bunker_fuel', 'vlsfo').toLowerCase());
  const [timeRange,    setTimeRange]     = useState('7d');
  const [modalSource,  setModalSource]   = useState(null); // null = closed, string = open with source
  const [focusedPort,  setFocusedPort]   = useState(null);

  const openWaitlistForm = (source = 'general') => setModalSource(source);

  const setActiveFuel = (f) => {
    setActiveFuelRaw(f);
    writeLS('fv_bunker_fuel', f);
  };

  // Mock data for CheapestPortFinder / SpreadHeatmap / ForwardPrices
  useEffect(() => {
    getBunkerData().then(d => setData(d)).catch(() => {});
  }, []);

  // Live Worker fetch with 8-second timeout and retry support
  useEffect(() => {
    if (!WORKER_URL) return;
    setLoadState('loading');
    const timeout = setTimeout(() => {
      setLoadState(prev => prev === 'loading' ? 'error' : prev);
    }, 8000);
    fetchLiveData().then(d => {
      clearTimeout(timeout);
      setLiveData(d || null);
      setLoadState(d ? 'live' : 'error');
    });
    return () => clearTimeout(timeout);
  }, [retryKey]);

  // Live table ports — placeholder rows (all prices null → em-dash) during loading/error
  const { tablePorts, tableOrder } = useMemo(() => {
    if (loadState === 'live' && liveData?.ports?.length) {
      const { ports, order } = buildTablePorts(liveData.ports);
      return { tablePorts: ports, tableOrder: order };
    }
    return { tablePorts: PLACEHOLDER_PORTS.ports, tableOrder: PLACEHOLDER_PORTS.order };
  }, [loadState, liveData]);

  // Live averages — null during loading/error so cards show em-dash
  const averages = useMemo(() => {
    if (loadState === 'live' && liveData?.averages) return liveData.averages;
    return null;
  }, [loadState, liveData]);

  const dataSinceDays = useMemo(() => {
    const since = liveData?.data_since || liveData?.history?.snapshots?.[0]?.date;
    if (!since) return 180;
    return Math.max(1, Math.floor((Date.now() - new Date(since)) / 86400000));
  }, [liveData]);

  // Mock ports/metrics for CheapestPortFinder + SpreadHeatmap (mock-only sections)
  const ports = data?.ports || PORTS_DATA;

  const isStale = loadState === 'live' && liveData?.stale === true;

  return (
    <div>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.moduleTitle}>Global Bunker Price Monitor</div>
          <div style={S.moduleSub}>Real-time prices across major bunkering hubs</div>
        </div>
        <div style={S.actions}>
          {!WORKER_URL && <span style={S.dataTag}>Demo Mode</span>}
          {WORKER_URL && loadState === 'loading' && (
            <div style={{ display:'flex', alignItems:'center', gap:7 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'#e9a020', animation:'bkPulse 1.5s ease-in-out infinite' }} />
              <span style={{ fontSize:11, color:'#8892a5', fontFamily:"'Figtree', sans-serif" }}>Checking latest prices…</span>
            </div>
          )}
          {WORKER_URL && loadState === 'live' && (
            <div style={{ display:'flex', alignItems:'center', gap:7 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'#2daa6e' }} />
              <span style={{ fontSize:11, color:'#2daa6e', fontFamily:"'Figtree', sans-serif" }}>
                Live{liveData?.updated_at ? ` · ${new Date(liveData.updated_at).toUTCString().slice(0, 22)}` : ''}
              </span>
            </div>
          )}
          {isStale && <span style={{ ...S.dataTag, background:'rgba(239,68,68,0.15)', color:'#ef4444' }}>⚠ Stale Data</span>}
          {WORKER_URL && loadState === 'error' && (
            <div style={{ display:'flex', alignItems:'center', gap:7 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'#ef4444' }} />
              <span style={{ fontSize:11, color:'#8892a5', fontFamily:"'Figtree', sans-serif" }}>Live feed unavailable</span>
              <button onClick={() => setRetryKey(k => k + 1)} style={{ background:'none', border:'none', color:'#e9a020', fontSize:11, cursor:'pointer', fontFamily:"'Figtree', sans-serif", padding:0 }}>· Retry</button>
            </div>
          )}
          {/* <button style={S.btn}>⬇ Export CSV</button>
          <button style={S.btn}>🔔 Price Alerts</button>
          <button style={{ ...S.btn, ...S.btnPrimary }}>⚙ Connect Live Feed</button> */}
        </div>
      </div>

      {/* Regional average hero strip — replaces old KPI bar */}
      <RegionalAvgStrip
        averages={averages}
        activeFuel={activeFuel}
        onFuelChange={setActiveFuel}
        loading={loadState === 'loading'}
      />

      {/* Hub map */}
      <div style={{ padding: '20px 28px 0' }}>
        <HubMap
          ports={liveData?.ports || []}
          onPortFocus={setFocusedPort}
          fuel={activeFuel}
          onFuelChange={setActiveFuel}
        />
      </div>

      {/* Time range selector */}
      <div style={{ padding: '12px 28px 0', display: 'flex', alignItems: 'center' }}>
        <TimeRangeChips selected={timeRange} onChange={setTimeRange} dataSinceDays={dataSinceDays} />
      </div>

      {/* SVG comparison chart */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '16px 18px 0' }}>
        <BunkerComparison
          ports={tablePorts}
          averages={averages}
          activeFuel={activeFuel}
          onFuelChange={setActiveFuel}
        />
      </div>

      <div style={S.content}>
        {/* Row 1: Port Finder + Heatmap */}
        <div className="fv-top-row">
          <CheapestPortFinder ports={ports} />
          <SpreadHeatmap ports={ports} />
        </div>

        {/* Row 2: Price table with accordion expansion */}
        <PriceTable
          ports={tablePorts}
          portOrder={tableOrder}
          activeFuel={activeFuel}
          loading={false}
          averages={averages}
          openWaitlistForm={openWaitlistForm}
        />

        {/* Row 3: Forward Prices */}
        <ForwardPrices openWaitlistForm={openWaitlistForm} />
      </div>

      {/* Premium teaser */}
      <div style={{ padding: '0 28px 20px' }}>
        <PremiumTeaser onOpen={() => openWaitlistForm('expanded_ports')} />
      </div>

      {modalSource !== null && (
        <ProWaitlistModal onClose={() => setModalSource(null)} interestSource={modalSource} />
      )}

      <div style={S.footer}>
        <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:11, color:'#8892a5' }}>
          FATHOMVOYAGE.COM · LAST UPDATED AT : {loadState === 'live' ? '' : 'DEMO'}
          {liveData?.updated_at ? ` · ${new Date(liveData.updated_at).toUTCString().split(' ').slice(0, 5).join(' ')}` : ''}
        </div>
        <div style={{ fontSize:11, color:'#8892a5' }}>Prices USD/mt · For reference only</div>
      </div>
    </div>
  );
}

export default function BunkerModule() {
  return (
    <BunkerErrorBoundary>
      <BunkerModuleInner />
    </BunkerErrorBoundary>
  );
}
